import { useMemo } from 'react';
import { Dialog, SelectField, TextInputField, TextareaField, Alert, Text } from 'evergreen-ui';
import { normalizeTime } from '../../utils/timeUtils';
import { useRole } from '../../contexts/RoleContext';

export default function TurniShiftModal({
  isOpen,
  onClose,
  onSave,
  shiftInputs,
  setShiftInputs,
  userName,
  collaboratorsList,
  turniData
}) {
  const { effectiveIsDirector: isDirector } = useRole();
  // Trova le persone assenti nell'orario e nel giorno selezionato e disabilita la loro selezione nel form
  const absentOperatorsObj = useMemo(() => {
    if (!shiftInputs.date) return {};
    const absent = {};
    
    // Converte orario "HH:MM" in frazione di ora per confronto 
    const timeToFloat = (hhmm) => {
      if (!hhmm) return null;
      const [h, m] = hhmm.split(':');
      return parseInt(h) + parseInt(m)/60;
    };
    
    const reqStart = timeToFloat(shiftInputs.start_time);
    const reqEnd = timeToFloat(shiftInputs.end_time);

    turniData.forEach((t) => {
      // Se il turno nel giorno corrispondente è assenza e sovrappone l'orario richiesto
      if (t.date === shiftInputs.date && ['ferie', 'permesso', 'malattia'].includes(t.shift_type)) {
        
        // Se l'assenza copre tutta la giornata, marchiamo assente
        if (!t.start_time && !t.end_time) {
          absent[t.operator_name] = t.shift_type;
        } else if (reqStart !== null && reqEnd !== null) {
          // Se la richiesta va a coprire il turno di assenza parziale
          const absStart = timeToFloat(t.start_time);
          const absEnd = timeToFloat(t.end_time);

          if (absStart !== null && absEnd !== null) {
            let modReqEnd = reqEnd <= reqStart ? reqEnd + 24 : reqEnd;
            let modAbsEnd = absEnd <= absStart ? absEnd + 24 : absEnd;
            
            let isOverlapping = false;
            // Sovrappone se: rStart < aEnd && rEnd > aStart
            if (reqStart < modAbsEnd && modReqEnd > absStart) {
              isOverlapping = true;
            }
            if (isOverlapping) {
              absent[t.operator_name] = t.shift_type;
            }
          }
        }
      }
    });
    return absent;
  }, [shiftInputs.date, shiftInputs.start_time, shiftInputs.end_time, turniData]);

  // Controlla se l'operatore attualmente selezionato è in ferie in quella data
  const isSelectedOperatorAbsent = !!absentOperatorsObj[shiftInputs.operator_name] || (!isDirector && !!absentOperatorsObj[userName]);

  const isEditMode = !!shiftInputs.id;

  return (
    <Dialog
      isShown={isOpen}
      title={isEditMode ? "Modifica turno" : "Inserisci turno"}
      onCloseComplete={onClose}
      confirmLabel={isEditMode ? "Aggiorna turno" : "Salva turno"}
      intent="success"
      onConfirm={onSave}
      isConfirmDisabled={isSelectedOperatorAbsent || !shiftInputs.shift_period}
    >
      {isSelectedOperatorAbsent && (
        <Alert intent="danger" title="Attenzione!" marginBottom={16}>
          <Text size={300} color="inherit">
            Il collaboratore selezionato risulta ASSENTE ({absentOperatorsObj[shiftInputs.operator_name] ? absentOperatorsObj[shiftInputs.operator_name].toUpperCase() : 'FERIE'}) nella data indicata e non può essere assegnato al turno.
          </Text>
        </Alert>
      )}

      {isDirector && (
        <SelectField 
          label="Seleziona collaboratore" 
          value={shiftInputs.operator_name}
          onChange={(e) => setShiftInputs({...shiftInputs, operator_name: e.target.value})}
          description={shiftInputs.date ? "I collaboratori segnati come *[ASSENTE]* non possono essere assegnati." : ""}
        >
          <option value="">-- Seleziona un collaboratore --</option>
          <option value={userName} disabled={!!absentOperatorsObj[userName]}>
            Assegna a me ({userName}) {absentOperatorsObj[userName] ? ` [${absentOperatorsObj[userName].toUpperCase()}]` : ''}
          </option>
          {collaboratorsList.map(collab => {
             const fullName = `${collab.nome || ''} ${collab.cognome || ''}`.trim() || collab.email;
             const isAbsent = absentOperatorsObj[fullName];
             return (
               <option key={collab.id} value={fullName} disabled={!!isAbsent}>
                 {fullName} {isAbsent ? ` [${isAbsent.toUpperCase()}]` : ''}
               </option>
             );
          })}
        </SelectField>
      )}
      
      <TextInputField
        label="Data"
        type="date"
        value={shiftInputs.date}
        onChange={(e) => setShiftInputs({ ...shiftInputs, date: e.target.value })}
      />

      <div style={{ display: 'flex', gap: '16px' }}>
        <TextInputField
          label="Ora di inizio"
          type="text"
          flex="1"
          placeholder="es. 9 oppure 09:30"
          value={shiftInputs.start_time}
          onChange={(e) => setShiftInputs({ ...shiftInputs, start_time: e.target.value })}
          onBlur={(e) => setShiftInputs({ ...shiftInputs, start_time: normalizeTime(e.target.value) })}
          required
        />
        <TextInputField
          label="Ora di fine"
          type="text"
          flex="1"
          placeholder="es. 17 oppure 17:30"
          value={shiftInputs.end_time}
          onChange={(e) => setShiftInputs({ ...shiftInputs, end_time: e.target.value })}
          onBlur={(e) => setShiftInputs({ ...shiftInputs, end_time: normalizeTime(e.target.value) })}
          required
        />
      </div>

      <SelectField label="Tipologia turno" value={shiftInputs.is_overtime ? 'true' : 'false'} onChange={e => setShiftInputs({...shiftInputs, is_overtime: e.target.value === 'true'})}>
        <option value="false">Turno standard</option>
        <option value="true">Straordinario</option>
      </SelectField>

      <SelectField
        label="Periodo *"
        value={shiftInputs.shift_period || ''}
        onChange={e => setShiftInputs({ ...shiftInputs, shift_period: e.target.value })}
        isInvalid={!shiftInputs.shift_period}
        validationMessage={!shiftInputs.shift_period ? 'Il periodo è obbligatorio' : undefined}
      >
        <option value="" disabled>-- Seleziona periodo --</option>
        <option value="coda_della_notte">Coda della notte</option>
        <option value="giorno">Giorno</option>
        <option value="notte">Notte</option>
      </SelectField>

      <TextareaField label="Note (facoltative)" value={shiftInputs.notes} onChange={e => setShiftInputs({...shiftInputs, notes: e.target.value})} />
    </Dialog>
  );
}